import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import {
  DropdownContainer,
  StyledButton,
  StyledCheckboxFormControl,
  StyledDivider,
  StyledSwitch,
  StyledText,
} from '@selfserve/react-ui';
import { IIndexSignature } from '@selfserve/react-utils';

import { Property } from '../types';

import { StyledSearchInput } from '../ui/admin-styled-components';

const StyledShowAllContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`;

const StyledListContainer = styled('div')`
  margin-top: 24px;
  width: calc(100% + 16px);
  label {
    display: flex;
    margin-bottom: 22px;
    p {
      font-size: 14px;
      font-weight: 600;
    }
    &:last-of-type {
      margin-bottom: 12px;
    }
  }
`;

const StyledPropertyFilterDivider = styled(StyledDivider)`
  margin: 0 0 16px;
  width: calc(100% + 32px);
  margin-left: -16px;
`;

interface Props {
  properties: Property[];
  onChange: (properties: Property[]) => void;
  value: Property[];
}

export const AdminPropertyFilter = ({ properties, onChange, value }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(properties.length === value.length);
  const [selected, setSelected] = useState<IIndexSignature<Property>>({});

  useEffect(() => {
    setSelected(value.reduce(arrayToObjectCallback, {}));
    onChange(value);
  }, []);

  const filteredProperties = useMemo(() => {
    if (search) {
      return properties.filter(
        (property) =>
          property.value.toLowerCase().includes(search.toLowerCase()) ||
          property.text.toLowerCase().includes(search.toLowerCase())
      );
    }
    return properties;
  }, [properties, search]);

  const arrayToObjectCallback = useCallback(
    (acc: IIndexSignature<Property>, curr: Property) => ({
      ...acc,
      [curr.value]: curr,
    }),
    []
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
    []
  );

  const handleShowAllClick = useCallback(
    () =>
      setShowAll((value) => {
        setSelected(() => {
          if (!value) return properties.reduce(arrayToObjectCallback, {});
          const mandatoryValues: IIndexSignature<Property> = {};
          properties.forEach((property) => {
            const { mandatory, value } = property;
            if (mandatory) mandatoryValues[value] = property;
          });
          return mandatoryValues;
        });
        return !value;
      }),
    [properties]
  );

  const handleSetSelected = useCallback(
    (property: Property) => () => {
      if (showAll) setShowAll(false);
      setSelected((prevValue) => {
        const newValue = { ...prevValue };
        if (newValue[property.value]) {
          delete newValue[property.value];
        } else {
          newValue[property.value] = property;
        }
        return newValue;
      });
    },
    [showAll]
  );

  const handleButtonClick = useCallback(
    () => onChange(Object.values(selected)),
    [selected]
  );

  return (
    <DropdownContainer displayText={t('common.edit-columns')}>
      <StyledSearchInput
        value={search}
        onChange={handleSearchChange}
        width={228}
      />
      <StyledShowAllContainer>
        <StyledText isSmall>{t('common.show-all')}</StyledText>
        <StyledSwitch isSmall checked={showAll} onClick={handleShowAllClick} />
      </StyledShowAllContainer>
      <StyledListContainer>
        <Scrollbars universal autoHeight autoHeightMin={0} autoHeightMax={178}>
          {filteredProperties.map((property) => {
            const { mandatory, value, text } = property;
            const checked = mandatory || !!selected[value];
            return (
              <StyledCheckboxFormControl
                onClick={handleSetSelected(property)}
                defaultChecked={checked}
                disabled={mandatory}
                checked={checked}
                label={text}
                key={value}
              />
            );
          })}
        </Scrollbars>
      </StyledListContainer>
      <StyledPropertyFilterDivider />
      <StyledButton
        withoutMargin
        isSmall
        fullWidth
        onClick={handleButtonClick}
        type="submit"
      >
        {t('common.select')}
        {` ${Object.keys(selected).length}`}
      </StyledButton>
    </DropdownContainer>
  );
};

AdminPropertyFilter.defaultProps = {
  properties: [],
};
